import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Divider, IconButton, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { ConfirmCancellationDialog } from "common/components";
import { Profile } from "features/userProfile";
import { PropsWithChildren, useState } from "react";
import { useUIStore } from "stores";

interface HeaderBarProps {
  nested?: boolean;
  title: string;
  needsConfirm?: boolean;
  onBack?: () => void;
  burgerColor?: string;
  canMinimizeDrawer?: boolean;
}

export default function HeaderBar({
  nested = false,
  title,
  needsConfirm = false,
  onBack,
  children,
  burgerColor,
  canMinimizeDrawer = true,
}: PropsWithChildren<HeaderBarProps>) {
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const drawerOpen =
    useUIStore((state) => state.drawerOpen) || !canMinimizeDrawer; // if drawer cannot be minimized it is always open

  const hasBackButton = drawerOpen && nested;

  return (
    <>
      <Toolbar>
        <DrawerToggleButton
          onClick={hasBackButton ? handleBackButtonClick : handleDrawerToggle}
          open={drawerOpen}
        >
          {hasBackButton ? (
            <ArrowBackIcon />
          ) : !canMinimizeDrawer ? (
            <></>
          ) : drawerOpen ? (
            <>
              <ChevronLeftIcon sx={{ color: burgerColor }} />
              <ChevronLeftIcon sx={{ ml: -2, color: burgerColor }} />
            </>
          ) : (
            <>
              <ChevronRightIcon sx={{ color: burgerColor }} />
              <ChevronRightIcon sx={{ ml: -2, color: burgerColor }} />
            </>
          )}
        </DrawerToggleButton>
        {drawerOpen && <Typography variant="h6">{title}</Typography>}
        {nested && (
          <ConfirmCancellationDialog
            open={confirmDialogOpen}
            onClose={handleConfirmationClose}
          />
        )}
        {children}
        <Profile />
      </Toolbar>
      <Divider />
    </>
  );

  function handleConfirmationClose() {
    setConfirmDialogOpen(false);
  }

  function handleBackButtonClick() {
    if (needsConfirm) {
      setConfirmDialogOpen(true);
    } else {
      if (onBack) onBack();
    }
  }

  function handleDrawerToggle() {
    const { closeDrawer, openDrawer } = useUIStore.getState();
    if (drawerOpen) {
      closeDrawer();
    } else {
      openDrawer();
    }
  }
}

const Toolbar = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  paddingTop: "env(safe-area-inset-top)",
});

const DrawerToggleButton = styled(IconButton)<{ open: boolean }>(
  ({ theme, open }) => ({
    margin: theme.spacing(2),
    width: 40,
    height: 40,
    ...(!open && {
      background: "white",
      boxShadow: theme.shadows[2],
      "&:hover": {
        background: "white",
      },
    }),
  })
);
